/**
 * NewsItemView view component.
 * @module components/theme/View/NewsItemView
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Container as SemanticContainer } from 'semantic-ui-react';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { flattenHTMLToAppURL } from '@plone/volto/helpers/Url/Url';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import config from '@plone/volto/registry';
import './NewsItemView.css'; // Aggiunto per gli stili
import CustomRenderBlocks from './CustomRenderBlocks';

/**
 * NewsItemView view component class.
 * @function NewsItemView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  if (isNaN(day)) return '';

  return `${day}-${month}-${year}`;
};

const getDateDisplay = (effectiveDate, expirationDate) => {
  if (!effectiveDate) return '';
  
  const formattedEffective = formatDate(effectiveDate);
  const formattedExpiration = formatDate(expirationDate);

  if (expirationDate && formattedExpiration) {
    return `Dal ${formattedEffective} al ${formattedExpiration}`;
  }
  return formattedEffective;
};

const NewsItemView = ({ content }) => {
  const Image = config.getComponent({ name: 'Image' }).component;
  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  // Componente comune per header (titolo, data, descrizione)
  const HeaderContent = () => (
    <div className="text-content">
      <h1 className="documentFirstHeading">
        {content.title}
        {content.subtitle && ` - ${content.subtitle}`}
      </h1>

      {content.effective && (
        <p className='dataNotizia'>{getDateDisplay(content.effective, content.expires)}</p>
      )}

      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
    </div>
  );

  // Componente comune per l'immagine
  const HeaderImage = () => (
    content.image && (
      <div className="header-image-wrapper">
        <Image
          className="header-image"
          alt={content.title}
          title={content.title}
          item={content}
          imageField="image"
          responsive={true}
        />
      </div>
    )
  );

  return hasBlocksData(content) ? (
    <Container id="page-document" className="view-wrapper newsitem-view">
      <HeaderContent />
      <HeaderImage />
      <RenderBlocks content={content} />
    </Container>
  ) : (
    <div className="view-wrapper">
      <Container>
        <HeaderContent />
        <HeaderImage />
        
        {content.text && (
          <div
            dangerouslySetInnerHTML={{
              __html: flattenHTMLToAppURL(content.text.data),
            }}
          />
        )}
      </Container>
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
NewsItemView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    text: PropTypes.shape({
      data: PropTypes.string,
    }),
  }).isRequired,
};

export default NewsItemView;